import { endOfYear, startOfYear } from 'date-fns';
import { Calendar as CalendarIcon, ChevronDown, ChevronRight, FileText, Search, X } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import type { DateRange } from 'react-day-picker';
import { useMatterJurisdiction } from '@/components/contexts/MatterJurisdictionContext';
import { EntityIcon } from '@/components/knowledge-graph/EntityIcon';
import { FilterSummaryChip } from '@/components/knowledge-graph/FilterSummaryChip';
import { OutputDisclaimer } from '@/components/shared/OutputDisclaimer';
import { Button } from '@/components/ui/button';
import { DateRangeCalendar, type DateRangePreset } from '@/components/ui/DateRangePicker';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import type { ChronologyEventLight } from '@/hooks/useChronology';
import type { CanonicalEntity, EntityRole } from '@/hooks/useEntities';
import { hasPrincipalRole, roleLabel, sideLabel } from '@/lib/role-labels';
import { cn } from '@/lib/utils';
import { formatRegionalDate, type RegionalDateLocale } from '@/utils/date-utils';
import { getStyleByColor } from './chronology-panel/styles';

// ── Role grouping ──────────────────────────────────────────────────

interface RoleGroup {
  key: string;
  label: string;
  roleTypes: Set<string>;
  showSideAlignment: boolean;
}

const ROLE_GROUPS: RoleGroup[] = [
  {
    key: 'parties',
    label: 'Parties & Counsel',
    roleTypes: new Set(['party']),
    showSideAlignment: true,
  },
  {
    key: 'amici',
    label: 'Amici',
    roleTypes: new Set(['amicus']),
    showSideAlignment: false,
  },
  {
    key: 'witnesses',
    label: 'Witnesses',
    roleTypes: new Set(['witness']),
    showSideAlignment: false,
  },
  {
    key: 'decision_makers',
    label: 'Decision Makers',
    roleTypes: new Set(['adjudicator', 'judge', 'arbitrator']),
    showSideAlignment: false,
  },
  {
    key: 'experts',
    label: 'Experts',
    roleTypes: new Set(['expert']),
    showSideAlignment: false,
  },
];

const COUNT_SLOT_CLASSNAME = 'inline-block min-w-[2ch] shrink-0 text-right tabular-nums';
const FLAT_ENTITY_LIST_CLASSNAME = 'w-full max-w-[44rem] space-y-2';
const ROLE_FILTER_BUTTON_CLASSNAME =
  'inline-flex max-w-full items-center rounded-md border text-xs font-normal cursor-pointer select-none transition-[background-color,color,opacity,box-shadow] focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-gray-300/45 focus-visible:ring-offset-1';
const ROLE_FILTER_STYLE = getStyleByColor('amber');
const OTHER_FILTER_TRIGGER_CLASSNAME =
  'h-8 justify-start gap-1.5 rounded-md border-transparent bg-transparent px-2.5 text-xs font-normal shadow-none transition-colors hover:bg-gray-50/80 hover:text-gray-700';
const OTHER_FILTER_ACTIVE_CLASSNAME = 'bg-gray-50/70 text-gray-700 hover:bg-gray-100/70';
const OTHER_FILTER_INACTIVE_CLASSNAME = 'text-gray-400';
const OTHER_FILTER_BADGE_CLASSNAME =
  'ml-0.5 inline-flex min-w-[1rem] items-center justify-center rounded-full bg-gray-100/80 px-1 text-[10px] text-gray-500 tabular-nums';
const OTHER_FILTER_SEPARATOR_CLASSNAME = 'hidden';

interface EntityAccentStyle {
  borderL: string;
  activeBorderL: string;
  hoverBorderL: string;
  text: string;
}

const ENTITY_ACCENTS: Record<string, EntityAccentStyle> = {
  person: {
    borderL: 'border-l-blue-100/80',
    activeBorderL: 'border-l-blue-300/80',
    hoverBorderL: 'hover:border-l-blue-200',
    text: 'text-sky-900/45',
  },
  organization: {
    borderL: 'border-l-amber-200/45',
    activeBorderL: 'border-l-amber-300/55',
    hoverBorderL: 'hover:border-l-amber-200/60',
    text: 'text-amber-950/35',
  },
  other: {
    borderL: 'border-l-slate-200',
    activeBorderL: 'border-l-slate-300',
    hoverBorderL: 'hover:border-l-slate-300',
    text: 'text-slate-600/50',
  },
};

function getEntityAccent(type: CanonicalEntity['entity_type']): EntityAccentStyle {
  return ENTITY_ACCENTS[type || 'other'] ?? ENTITY_ACCENTS.other;
}

function normalizeFacetName(name: string): string {
  return name.trim().toLowerCase();
}

function dateRangeHasValue(dateRange: DateRange | undefined): boolean {
  return Boolean(dateRange?.from || dateRange?.to);
}

function dateInRange(date: Date, dateRange: DateRange | undefined): boolean {
  if (!dateRangeHasValue(dateRange)) return true;
  const from = dateRange?.from ? new Date(dateRange.from) : null;
  const to = dateRange?.to ? new Date(dateRange.to) : null;
  if (from) from.setHours(0, 0, 0, 0);
  if (to) to.setHours(23, 59, 59, 999);
  const time = date.getTime();
  if (from && time < from.getTime()) return false;
  if (to && time > to.getTime()) return false;
  return true;
}

function hasDisplayLegalRepresentativeRole(entity: CanonicalEntity): boolean {
  return entity.roles.some(role => role.role_type === 'legal_representative') && !hasPrincipalRole(entity.roles);
}

function getEntityGroups(entity: CanonicalEntity): string[] {
  const groups: string[] = [];
  for (const group of ROLE_GROUPS) {
    if (entity.roles.some(r => group.roleTypes.has(r.role_type))) groups.push(group.key);
  }
  return groups;
}

function getRoleForGroup(entity: CanonicalEntity, groupKey: string): EntityRole | undefined {
  const group = ROLE_GROUPS.find(g => g.key === groupKey);
  if (!group) return entity.roles[0];
  return entity.roles.find(r => group.roleTypes.has(r.role_type))
    ?? ((groupKey === 'parties' || groupKey === 'amici') && !hasPrincipalRole(entity.roles)
      ? entity.roles.find(r => r.role_type === 'legal_representative')
      : undefined)
    ?? entity.roles[0];
}

function getEntitySideForGroup(entity: CanonicalEntity, groupKey: string): 'claimant' | 'respondent' | 'neutral' {
  const role = getRoleForGroup(entity, groupKey);
  if (role?.side_alignment === 'claimant_side') return 'claimant';
  if (role?.side_alignment === 'respondent_side') return 'respondent';
  return 'neutral';
}

function getLegalRepresentativeGroupKeys(
  entity: CanonicalEntity,
  entityById: ReadonlyMap<string, CanonicalEntity>,
): string[] {
  if (!hasDisplayLegalRepresentativeRole(entity)) return [];

  const representedEntityIds = new Set<string>();
  for (const rel of entity.relationships) {
    if (rel.relationship_type === 'represents') {
      representedEntityIds.add(rel.target_entity_id);
    }
    if (rel.relationship_type !== 'member_of') continue;
    const parent = entityById.get(rel.target_entity_id);
    if (!parent) continue;
    for (const parentRel of parent.relationships) {
      if (parentRel.relationship_type === 'represents') {
        representedEntityIds.add(parentRel.target_entity_id);
      }
    }
  }

  const groupKeys = new Set<string>();
  for (const targetId of representedEntityIds) {
    const target = entityById.get(targetId);
    if (!target) continue;
    if (target.roles.some(role => role.role_type === 'party')) groupKeys.add('parties');
    if (target.roles.some(role => role.role_type === 'amicus')) groupKeys.add('amici');
  }

  if (groupKeys.size === 0) {
    groupKeys.add('parties');
  }

  return Array.from(groupKeys);
}

function getEntityDisplayGroupKeys(
  entity: CanonicalEntity,
  entityById: ReadonlyMap<string, CanonicalEntity>,
): Set<string> {
  const groupKeys = new Set<string>(getEntityGroups(entity));
  for (const key of getLegalRepresentativeGroupKeys(entity, entityById)) {
    groupKeys.add(key);
  }
  if (groupKeys.size === 0) {
    groupKeys.add('other');
  }
  return groupKeys;
}

// ── Hierarchy helpers ─────────────────────────────────────────────

interface PartyTree {
  party: CanonicalEntity;
  counsel: { firm: CanonicalEntity; attorneys: CanonicalEntity[] }[];
  /** Attorneys who represent this party but have no firm */
  directAttorneys: CanonicalEntity[];
}

function buildPartyTrees(
  entities: CanonicalEntity[],
  side: 'claimant' | 'respondent' | 'neutral',
): PartyTree[] {
  const parties = entities.filter(e =>
    e.roles.some(r => r.role_type === 'party') &&
    getEntitySideForGroup(e, 'parties') === side
  );

  const legalReps = entities.filter(e =>
    hasDisplayLegalRepresentativeRole(e) &&
    getEntitySideForGroup(e, 'parties') === side
  );

  // For each legal rep, find who they represent and their firm
  const representedBy = new Map<string, CanonicalEntity[]>(); // party_id → counsel entities
  const firmMembers = new Map<string, CanonicalEntity[]>();   // firm_id → attorney entities

  for (const rep of legalReps) {
    for (const rel of rep.relationships) {
      if (rel.relationship_type === 'represents') {
        const list = representedBy.get(rel.target_entity_id) || [];
        list.push(rep);
        representedBy.set(rel.target_entity_id, list);
      }
      if (rel.relationship_type === 'member_of') {
        const list = firmMembers.get(rel.target_entity_id) || [];
        list.push(rep);
        firmMembers.set(rel.target_entity_id, list);
      }
    }
  }

  const trees: PartyTree[] = [];

  for (const party of parties) {
    const repsForParty = representedBy.get(party.id) || [];

    // Separate firms from individual attorneys
    const firms: { firm: CanonicalEntity; attorneys: CanonicalEntity[] }[] = [];
    const directAttorneys: CanonicalEntity[] = [];

    // Find firms (orgs that represent this party)
    const firmEntities = repsForParty.filter(r => r.entity_type === 'organization');
    const personReps = repsForParty.filter(r => r.entity_type !== 'organization');

    for (const firm of firmEntities) {
      const attorneys = firmMembers.get(firm.id) || [];
      firms.push({ firm, attorneys });
    }

    // Person reps who aren't member_of any firm representing this party
    const firmIds = new Set(firmEntities.map(f => f.id));
    for (const person of personReps) {
      const isMemberOfFirm = person.relationships.some(
        r => r.relationship_type === 'member_of' && firmIds.has(r.target_entity_id)
      );
      if (!isMemberOfFirm) {
        directAttorneys.push(person);
      }
    }

    trees.push({ party, counsel: firms, directAttorneys });
  }

  return trees;
}

function buildPrincipalTrees(
  entities: CanonicalEntity[],
  principalRoleTypes: ReadonlySet<string>,
): PartyTree[] {
  const principals = entities.filter(e =>
    e.roles.some(r => principalRoleTypes.has(r.role_type))
  );

  const legalReps = entities.filter(e =>
    hasDisplayLegalRepresentativeRole(e)
  );

  const representedBy = new Map<string, CanonicalEntity[]>();
  const firmMembers = new Map<string, CanonicalEntity[]>();

  for (const rep of legalReps) {
    for (const rel of rep.relationships) {
      if (rel.relationship_type === 'represents') {
        const list = representedBy.get(rel.target_entity_id) || [];
        list.push(rep);
        representedBy.set(rel.target_entity_id, list);
      }
      if (rel.relationship_type === 'member_of') {
        const list = firmMembers.get(rel.target_entity_id) || [];
        list.push(rep);
        firmMembers.set(rel.target_entity_id, list);
      }
    }
  }

  const trees: PartyTree[] = [];

  for (const principal of principals) {
    const repsForPrincipal = representedBy.get(principal.id) || [];
    const firms: { firm: CanonicalEntity; attorneys: CanonicalEntity[] }[] = [];
    const directAttorneys: CanonicalEntity[] = [];

    const firmEntities = repsForPrincipal.filter(r => r.entity_type === 'organization');
    const personReps = repsForPrincipal.filter(r => r.entity_type !== 'organization');

    for (const firm of firmEntities) {
      const attorneys = firmMembers.get(firm.id) || [];
      firms.push({ firm, attorneys });
    }

    const firmIds = new Set(firmEntities.map(f => f.id));
    for (const person of personReps) {
      const isMemberOfFirm = person.relationships.some(
        r => r.relationship_type === 'member_of' && firmIds.has(r.target_entity_id)
      );
      if (!isMemberOfFirm) {
        directAttorneys.push(person);
      }
    }

    trees.push({ party: principal, counsel: firms, directAttorneys });
  }

  return trees;
}

function collectPartyTreeEntityIds(trees: PartyTree[]): Set<string> {
  const ids = new Set<string>();
  for (const tree of trees) {
    ids.add(tree.party.id);
    for (const { firm, attorneys } of tree.counsel) {
      ids.add(firm.id);
      for (const attorney of attorneys) {
        ids.add(attorney.id);
      }
    }
    for (const attorney of tree.directAttorneys) {
      ids.add(attorney.id);
    }
  }
  return ids;
}

// ── Components ─────────────────────────────────────────────────────

interface EntitiesPanelProps {
  entities: CanonicalEntity[];
  events?: ChronologyEventLight[];
  sourceNameByDocumentGuid?: ReadonlyMap<string, string>;
  onEntitySelect?: (entity: CanonicalEntity | null) => void;
}

interface EntityEventFacets {
  sourceNames: Set<string>;
  dates: Date[];
}

interface SourceSelectionPopoverProps {
  counts: ReadonlyMap<string, number>;
  activeSources: ReadonlySet<string>;
  onToggleSource: (source: string) => void;
  onClearSources: () => void;
}

interface EntityFilterSummaryProps {
  entitiesCount: number;
  filteredEntitiesCount: number;
  hasActiveFilters: boolean;
  activeSources: ReadonlySet<string>;
  dateRange: DateRange | undefined;
  locale: RegionalDateLocale;
  onToggleSource: (source: string) => void;
  onClearDateRange: () => void;
  onClearAllFilters: () => void;
}

function SourceSelectionPopover({
  counts,
  activeSources,
  onToggleSource,
  onClearSources,
}: SourceSelectionPopoverProps) {
  const [search, setSearch] = useState('');
  const sources = useMemo(() => {
    const query = search.trim().toLowerCase();
    return Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .filter(([name]) => !query || name.toLowerCase().includes(query));
  }, [counts, search]);

  return (
    <Popover onOpenChange={open => { if (!open) setSearch(''); }}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn(
            OTHER_FILTER_TRIGGER_CLASSNAME,
            activeSources.size > 0 ? OTHER_FILTER_ACTIVE_CLASSNAME : OTHER_FILTER_INACTIVE_CLASSNAME,
          )}
        >
          <FileText className="h-3.5 w-3.5 shrink-0" />
          Source
          {activeSources.size > 0 && (
            <span className={OTHER_FILTER_BADGE_CLASSNAME}>{activeSources.size}</span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[260px] p-0" align="start" side="bottom" sideOffset={4}>
        <div className="border-b border-gray-100 p-2">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 h-3 w-3 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={event => setSearch(event.target.value)}
              placeholder="Search sources..."
              className="w-full rounded-sm border border-gray-200 bg-white py-1 pl-7 pr-2 text-xs placeholder:text-gray-400 focus:outline-hidden focus:ring-1 focus:ring-gray-300"
            />
          </div>
        </div>
        <div className="max-h-[240px] overflow-y-auto py-1">
          {sources.length === 0 ? (
            <p className="px-3 py-3 text-xs text-gray-400">No matching sources.</p>
          ) : (
            sources.map(([name, count]) => {
              const active = activeSources.has(name);
              return (
                <button
                  type="button"
                  key={name}
                  onClick={() => onToggleSource(name)}
                  className={cn(
                    'flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs transition-colors hover:bg-gray-50',
                    active && 'bg-gray-50',
                  )}
                >
                  <span className={cn(
                    'flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-sm border transition-colors',
                    active ? 'border-gray-600 bg-gray-600' : 'border-gray-300',
                  )}>
                    {active && <span className="text-[9px] leading-none text-white">&#10003;</span>}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-gray-700">{name}</span>
                  <span className={cn(COUNT_SLOT_CLASSNAME, 'text-[10px] text-gray-400')}>{count}</span>
                </button>
              );
            })
          )}
        </div>
        {activeSources.size > 0 && (
          <div className="border-t border-gray-100 p-1.5">
            <button type="button" onClick={onClearSources} className="w-full py-0.5 text-center text-[11px] font-medium text-gray-500 hover:text-gray-700">
              Clear selection
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

function EntityFilterSummary({
  entitiesCount,
  filteredEntitiesCount,
  hasActiveFilters,
  activeSources,
  dateRange,
  locale,
  onToggleSource,
  onClearDateRange,
  onClearAllFilters,
}: EntityFilterSummaryProps) {
  const summaryLabel = filteredEntitiesCount === entitiesCount
    ? `Showing all ${entitiesCount} ${entitiesCount === 1 ? 'entity' : 'entities'}`
    : `Showing ${filteredEntitiesCount} of ${entitiesCount} ${entitiesCount === 1 ? 'entity' : 'entities'}`;

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="shrink-0 text-[11px] text-gray-500">{summaryLabel}</span>
      {hasActiveFilters && (
        <button
          type="button"
          onClick={onClearAllFilters}
          className="shrink-0 text-[11px] font-medium text-gray-500 hover:text-gray-700"
        >
          Clear all filters
        </button>
      )}
      {Array.from(activeSources).map(name => (
        <FilterSummaryChip key={name} label="Source" onRemove={() => onToggleSource(name)}>
          {name}
        </FilterSummaryChip>
      ))}
      {dateRangeHasValue(dateRange) && (
        <FilterSummaryChip icon={<CalendarIcon className="h-2.5 w-2.5 text-gray-300" />} onRemove={onClearDateRange}>
          {dateRange?.from && dateRange.to
            ? `${formatRegionalDate(dateRange.from, locale)} - ${formatRegionalDate(dateRange.to, locale)}`
            : dateRange?.from
              ? `From ${formatRegionalDate(dateRange.from, locale)}`
              : dateRange?.to ? `To ${formatRegionalDate(dateRange.to, locale)}` : null}
        </FilterSummaryChip>
      )}
    </div>
  );
}

function EntityCard({
  entity,
  role,
  isSelected,
  onSelect,
  indent = 0,
}: {
  entity: CanonicalEntity;
  role?: EntityRole;
  isSelected: boolean;
  onSelect: () => void;
  indent?: number;
}) {
  const accent = getEntityAccent(entity.entity_type);

  return (
    <div style={indent > 0 ? { paddingLeft: indent * 12 } : undefined}>
      <button
        type="button"
        onClick={onSelect}
        className={cn(
          'group w-full overflow-hidden rounded-md border border-l-2 px-3 py-2 text-left transition-colors hover:bg-gray-50',
          isSelected
            ? cn('border-gray-100 bg-gray-50', accent.activeBorderL)
            : cn('border-gray-100 bg-white hover:border-gray-200', accent.borderL, accent.hoverBorderL),
        )}
      >
        <div className="flex items-center gap-2.5">
          <EntityIcon type={entity.entity_type} size="sm" />
          <div className="min-w-0 flex-1">
            <p className="text-xs font-medium text-gray-800 leading-snug truncate">{entity.name}</p>
            {role && (
              <p className={cn('mt-0.5 truncate text-[10px] font-medium leading-4', accent.text)}>
                {roleLabel(role.role_type)}
              </p>
            )}
          </div>
        </div>
      </button>
    </div>
  );
}

function PartyTreeSection({
  tree,
  selectedId,
  onSelect,
}: {
  tree: PartyTree;
  selectedId: string | null;
  onSelect: (entity: CanonicalEntity) => void;
}) {
  const hasCounsel = tree.counsel.length > 0 || tree.directAttorneys.length > 0;
  const [counselExpanded, setCounselExpanded] = useState(false);
  const partyRole = tree.party.roles.find(r => ['party', 'amicus'].includes(r.role_type));
  const counselCount = tree.counsel.reduce((n, c) => n + 1 + c.attorneys.length, 0) + tree.directAttorneys.length;
  const isPartySelected = selectedId === tree.party.id;
  const accent = getEntityAccent(tree.party.entity_type);

  return (
    <div
      className={cn(
        'overflow-hidden rounded-md border border-l-2 transition-colors',
        isPartySelected
          ? cn('border-gray-100 bg-gray-50', accent.activeBorderL)
          : cn('border-gray-100 bg-white hover:border-gray-200 hover:bg-gray-50', accent.borderL, accent.hoverBorderL),
      )}
    >
      {/* Party header */}
      <button
        type="button"
        onClick={() => onSelect(tree.party)}
        className="w-full text-left px-3 py-2"
      >
        <div className="flex items-center gap-2.5">
          <EntityIcon type={tree.party.entity_type} size="sm" />
          <div className="min-w-0 flex-1">
            <p className="text-xs font-medium text-gray-800 leading-snug truncate">{tree.party.name}</p>
            {partyRole && (
              <p className={cn('mt-0.5 truncate text-[10px] font-medium leading-4', accent.text)}>
                {roleLabel(partyRole.role_type)}
              </p>
            )}
          </div>
        </div>
      </button>

      {/* Counsel nested inside */}
      {hasCounsel && (
        <div className="border-t border-gray-100 bg-white/80">
          <button
            type="button"
            onClick={() => setCounselExpanded(prev => !prev)}
            className="flex items-center gap-1 px-3 py-1.5 w-full text-left text-[10px] text-gray-400 hover:text-gray-600 transition-colors"
          >
            {counselExpanded
              ? <ChevronDown className="h-3 w-3" />
              : <ChevronRight className="h-3 w-3" />
            }
            <span>Counsel ({counselCount})</span>
          </button>
          {counselExpanded && (
            <div className="px-2 pb-2 space-y-1">
              {tree.counsel.map(({ firm, attorneys }) => (
                <div key={firm.id} className="space-y-1">
                  <EntityCard
                    entity={firm}
                    role={firm.roles.find(r => r.role_type === 'legal_representative')}
                    isSelected={selectedId === firm.id}
                    onSelect={() => onSelect(firm)}
                  />
                  {attorneys.map(attorney => (
                    <EntityCard
                      key={attorney.id}
                      entity={attorney}
                      role={attorney.roles.find(r => r.role_type === 'legal_representative')}
                      isSelected={selectedId === attorney.id}
                      onSelect={() => onSelect(attorney)}
                      indent={1}
                    />
                  ))}
                </div>
              ))}
              {tree.directAttorneys.map(attorney => (
                <EntityCard
                  key={attorney.id}
                  entity={attorney}
                  role={attorney.roles.find(r => r.role_type === 'legal_representative')}
                  isSelected={selectedId === attorney.id}
                  onSelect={() => onSelect(attorney)}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function HierarchicalSideSection({
  entities,
  selectedId,
  onSelect,
}: {
  entities: CanonicalEntity[];
  selectedId: string | null;
  onSelect: (entity: CanonicalEntity) => void;
}) {
  const { jurisdiction } = useMatterJurisdiction();
  const claimantTrees = useMemo(() => buildPartyTrees(entities, 'claimant'), [entities]);
  const respondentTrees = useMemo(() => buildPartyTrees(entities, 'respondent'), [entities]);
  const neutralTrees = useMemo(() => buildPartyTrees(entities, 'neutral'), [entities]);
  const neutralTreeEntityIds = useMemo(
    () => collectPartyTreeEntityIds(neutralTrees),
    [neutralTrees],
  );
  const neutralEntities = useMemo(() =>
    entities.filter(e => (
      (
        e.roles.some(r => ['party', 'amicus'].includes(r.role_type))
        || hasDisplayLegalRepresentativeRole(e)
      ) &&
      getEntitySideForGroup(e, 'parties') === 'neutral' &&
      !neutralTreeEntityIds.has(e.id)
    )),
    [entities, neutralTreeEntityIds],
  );

  const hasSides = claimantTrees.length > 0 || respondentTrees.length > 0;
  const hasNeutralHierarchy = neutralTrees.length > 0 || neutralEntities.length > 0;

  if (!hasSides && !hasNeutralHierarchy) {
    // No alignment info — render flat
    return (
      <div className={FLAT_ENTITY_LIST_CLASSNAME}>
        {entities.map(entity => (
          <EntityCard
            key={entity.id}
            entity={entity}
            role={getRoleForGroup(entity, 'parties')}
            isSelected={selectedId === entity.id}
            onSelect={() => onSelect(entity)}
          />
        ))}
      </div>
    );
  }

  if (!hasSides) {
    return (
      <div className="space-y-2">
        <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1.5">
          {sideLabel('neutral', jurisdiction.ui_labels)}
        </p>
        <div className={FLAT_ENTITY_LIST_CLASSNAME}>
          {neutralTrees.map(tree => (
            <PartyTreeSection
              key={tree.party.id}
              tree={tree}
              selectedId={selectedId}
              onSelect={onSelect}
            />
          ))}
          {neutralEntities.map(entity => (
            <EntityCard
              key={entity.id}
              entity={entity}
              role={getRoleForGroup(entity, 'parties')}
              isSelected={selectedId === entity.id}
              onSelect={() => onSelect(entity)}
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex gap-4">
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1.5">{sideLabel('claimant_side', jurisdiction.ui_labels)}</p>
          <div className="space-y-2">
            {claimantTrees.length > 0 ? claimantTrees.map(tree => (
              <PartyTreeSection
                key={tree.party.id}
                tree={tree}
                selectedId={selectedId}
                onSelect={onSelect}
              />
            )) : (
              <p className="text-[10px] text-gray-300 py-2">None</p>
            )}
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1.5">{sideLabel('respondent_side', jurisdiction.ui_labels)}</p>
          <div className="space-y-2">
            {respondentTrees.length > 0 ? respondentTrees.map(tree => (
              <PartyTreeSection
                key={tree.party.id}
                tree={tree}
                selectedId={selectedId}
                onSelect={onSelect}
              />
            )) : (
              <p className="text-[10px] text-gray-300 py-2">None</p>
            )}
          </div>
        </div>
      </div>
      {(neutralTrees.length > 0 || neutralEntities.length > 0) && (
        <div className="space-y-2">
          <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">
            {sideLabel('neutral', jurisdiction.ui_labels)}
          </p>
          <div className={FLAT_ENTITY_LIST_CLASSNAME}>
            {neutralTrees.map(tree => (
              <PartyTreeSection
                key={tree.party.id}
                tree={tree}
                selectedId={selectedId}
                onSelect={onSelect}
              />
            ))}
            {neutralEntities.map(entity => (
              <EntityCard
                key={entity.id}
                entity={entity}
                role={getRoleForGroup(entity, 'parties')}
                isSelected={selectedId === entity.id}
                onSelect={() => onSelect(entity)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function HierarchicalPrincipalSection({
  entities,
  selectedId,
  onSelect,
  groupKey,
  principalRoleTypes,
}: {
  entities: CanonicalEntity[];
  selectedId: string | null;
  onSelect: (entity: CanonicalEntity) => void;
  groupKey: string;
  principalRoleTypes: ReadonlySet<string>;
}) {
  const trees = useMemo(
    () => buildPrincipalTrees(entities, principalRoleTypes),
    [entities, principalRoleTypes],
  );

  if (trees.length === 0) {
    return (
      <div className={FLAT_ENTITY_LIST_CLASSNAME}>
        {entities.map(entity => (
          <EntityCard
            key={entity.id}
            entity={entity}
            role={getRoleForGroup(entity, groupKey)}
            isSelected={selectedId === entity.id}
            onSelect={() => onSelect(entity)}
          />
        ))}
      </div>
    );
  }

  return (
    <div className={FLAT_ENTITY_LIST_CLASSNAME}>
      {trees.map(tree => (
        <PartyTreeSection
          key={tree.party.id}
          tree={tree}
          selectedId={selectedId}
          onSelect={onSelect}
        />
      ))}
    </div>
  );
}

export function EntitiesPanel({
  entities,
  events = [],
  sourceNameByDocumentGuid,
  onEntitySelect,
}: EntitiesPanelProps) {
  const { locale } = useMatterJurisdiction();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilters, setActiveFilters] = useState<Set<string>>(new Set());
  const [activeSources, setActiveSources] = useState<Set<string>>(new Set());
  const [dateRange, setDateRange] = useState<DateRange | undefined>(undefined);
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set(['other']));

  const entityEventFacets = useMemo(() => {
    const idsByName = new Map<string, string[]>();
    const facets = new Map<string, EntityEventFacets>();

    for (const entity of entities) {
      const sourceNames = new Set<string>();
      for (const documentGuid of entity.document_guids) {
        const sourceName = sourceNameByDocumentGuid?.get(documentGuid)?.trim();
        if (sourceName) sourceNames.add(sourceName);
      }
      facets.set(entity.id, {
        sourceNames,
        dates: [],
      });
      for (const name of [entity.name, ...entity.aliases]) {
        const key = normalizeFacetName(name);
        if (!key) continue;
        const ids = idsByName.get(key) ?? [];
        ids.push(entity.id);
        idsByName.set(key, ids);
      }
    }

    for (const event of events) {
      const entityIds = new Set<string>();
      for (const name of event.entity_names || []) {
        const ids = idsByName.get(normalizeFacetName(name)) ?? [];
        for (const id of ids) entityIds.add(id);
      }
      if (entityIds.size === 0) continue;

      for (const id of entityIds) {
        const facet = facets.get(id);
        if (!facet) continue;
        for (const sourceDate of event.source_document_dates || []) {
          facet.dates.push(sourceDate);
        }
      }
    }

    return facets;
  }, [entities, events, sourceNameByDocumentGuid]);

  const eventYears = useMemo(() => {
    const years = new Set<number>();
    for (const facet of entityEventFacets.values()) {
      for (const date of facet.dates) years.add(date.getFullYear());
    }
    return Array.from(years).sort();
  }, [entityEventFacets]);

  const yearPresets = useMemo<DateRangePreset[]>(
    () => eventYears.map(year => {
      const date = new Date(year, 0, 1);
      return {
        id: String(year),
        label: String(year),
        range: { from: startOfYear(date), to: endOfYear(date) },
      };
    }),
    [eventYears],
  );

  const searchedEntities = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    if (!q) return entities;
    return entities.filter(ent => {
      if (ent.name.toLowerCase().includes(q)) return true;
      if (ent.aliases.some(a => a.toLowerCase().includes(q))) return true;
      if (ent.description?.toLowerCase().includes(q)) return true;
      return false;
    });
  }, [entities, searchQuery]);

  const entityMatchesDateRange = useCallback((entity: CanonicalEntity): boolean => {
    if (!dateRangeHasValue(dateRange)) return true;
    const facet = entityEventFacets.get(entity.id);
    return !!facet?.dates.some(date => dateInRange(date, dateRange));
  }, [dateRange, entityEventFacets]);

  const entitiesBeforeSourceFilter = useMemo(
    () => searchedEntities.filter(entity => entityMatchesDateRange(entity)),
    [entityMatchesDateRange, searchedEntities],
  );

  const sourceOptionEntities = useMemo(() => {
    if (activeFilters.size === 0) return entitiesBeforeSourceFilter;

    const entityById = new Map(entitiesBeforeSourceFilter.map(entity => [entity.id, entity]));
    return entitiesBeforeSourceFilter.filter(entity => {
      const groupKeys = getEntityDisplayGroupKeys(entity, entityById);
      for (const key of activeFilters) {
        if (groupKeys.has(key)) return true;
      }
      return false;
    });
  }, [activeFilters, entitiesBeforeSourceFilter]);

  const sourceCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const entity of sourceOptionEntities) {
      const facet = entityEventFacets.get(entity.id);
      if (!facet) continue;
      for (const sourceName of facet.sourceNames) {
        counts.set(sourceName, (counts.get(sourceName) ?? 0) + 1);
      }
    }
    for (const sourceName of activeSources) {
      if (!counts.has(sourceName)) counts.set(sourceName, 0);
    }
    return counts;
  }, [activeSources, entityEventFacets, sourceOptionEntities]);

  const entityMatchesSources = useCallback((entity: CanonicalEntity): boolean => {
    if (activeSources.size === 0) return true;
    const facet = entityEventFacets.get(entity.id);
    if (!facet) return false;
    for (const sourceName of activeSources) {
      if (facet.sourceNames.has(sourceName)) return true;
    }
    return false;
  }, [activeSources, entityEventFacets]);

  const filteredEntities = useMemo(
    () => sourceOptionEntities.filter(entity => entityMatchesSources(entity)),
    [entityMatchesSources, sourceOptionEntities],
  );

  const entitiesAfterEvidenceFilters = useMemo(
    () => entitiesBeforeSourceFilter.filter(entity => entityMatchesSources(entity)),
    [entitiesBeforeSourceFilter, entityMatchesSources],
  );

  const groupedEntities = useMemo(() => {
    const groups = new Map<string, CanonicalEntity[]>();
    for (const group of ROLE_GROUPS) {
      groups.set(group.key, []);
    }
    groups.set('other', []);
    const entityById = new Map(filteredEntities.map(entity => [entity.id, entity]));

    for (const entity of filteredEntities) {
      for (const groupKey of getEntityDisplayGroupKeys(entity, entityById)) {
        groups.get(groupKey)?.push(entity);
      }
    }

    return groups;
  }, [filteredEntities]);

  const handleSelect = useCallback((entity: CanonicalEntity) => {
    setSelectedId(prev => {
      const next = prev === entity.id ? null : entity.id;
      onEntitySelect?.(next ? entity : null);
      return next;
    });
  }, [onEntitySelect]);

  const toggleGroup = useCallback((key: string) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }, []);

  const toggleFilter = useCallback((key: string) => {
    setActiveFilters(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }, []);

  const toggleSource = useCallback((source: string) => {
    setActiveSources(prev => {
      const next = new Set(prev);
      if (next.has(source)) next.delete(source); else next.add(source);
      return next;
    });
  }, []);

  const allGroups = useMemo(() => [
    ...ROLE_GROUPS,
    { key: 'other', label: 'Other', roleTypes: new Set<string>(), showSideAlignment: false },
  ], []);

  const groupCounts = useMemo(() => {
    const counts = new Map<string, number>();
    const entityById = new Map(entitiesAfterEvidenceFilters.map(entity => [entity.id, entity]));
    for (const group of allGroups) counts.set(group.key, 0);
    for (const entity of entitiesAfterEvidenceFilters) {
      for (const groupKey of getEntityDisplayGroupKeys(entity, entityById)) {
        counts.set(groupKey, (counts.get(groupKey) ?? 0) + 1);
      }
    }
    return counts;
  }, [allGroups, entitiesAfterEvidenceFilters]);

  const hasActiveFilters = searchQuery.trim().length > 0
    || activeFilters.size > 0
    || activeSources.size > 0
    || dateRangeHasValue(dateRange);

  const clearDateRange = useCallback(() => {
    setDateRange(undefined);
  }, []);

  const clearAllFilters = useCallback(() => {
    setSearchQuery('');
    setActiveFilters(new Set());
    setActiveSources(new Set());
    setDateRange(undefined);
  }, []);

  return (
    <div className="flex h-full">
      <div className="subtle-scrollbar flex-1 overflow-y-scroll">
        {entities.length > 0 ? (
          <>
            {/* Search */}
            <div className="sticky top-0 z-10 border-b border-gray-100 bg-white px-6 pt-3 pb-4">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  placeholder="Search entities..."
                  className="w-full pl-8 pr-8 py-1.5 text-sm border border-gray-200 rounded-lg bg-white focus:outline-hidden focus:ring-1 focus:ring-gray-300 focus:border-gray-300 placeholder:text-gray-400"
                />
                {searchQuery && (
                  <button type="button" onClick={() => setSearchQuery('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
              <div className="mt-4 space-y-2">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">Role groups</p>
                <div className="flex max-w-[88rem] flex-wrap items-center gap-x-6 gap-y-2">
                  {allGroups.map(g => {
                    const count = groupCounts.get(g.key) ?? 0;
                    const active = activeFilters.has(g.key);
                    return (
                      <button
                        type="button"
                        key={g.key}
                        onClick={() => toggleFilter(g.key)}
                        aria-pressed={active}
                        className={cn(
                          ROLE_FILTER_BUTTON_CLASSNAME,
                          'gap-1.5 px-2 py-0.5',
                          active ? ROLE_FILTER_STYLE.activePill : ROLE_FILTER_STYLE.pill,
                          active ? 'opacity-90' : 'opacity-85 hover:opacity-100',
                        )}
                      >
                        <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', active ? ROLE_FILTER_STYLE.activeDot : ROLE_FILTER_STYLE.dot)} />
                        <span className="min-w-0 truncate">{g.label}</span>
                        <span className={cn(COUNT_SLOT_CLASSNAME, 'text-[10px]', active ? 'opacity-80' : 'opacity-60')}>{count}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="mt-4 space-y-2">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">Evidence filters</p>
                <div className="flex flex-wrap items-center gap-x-2.5 gap-y-2">
                  <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          className={cn(
                            OTHER_FILTER_TRIGGER_CLASSNAME,
                            dateRangeHasValue(dateRange) ? OTHER_FILTER_ACTIVE_CLASSNAME : OTHER_FILTER_INACTIVE_CLASSNAME,
                          )}
                        >
                          <CalendarIcon className="h-3.5 w-3.5 shrink-0" />
                          {dateRange?.from ? (
                            dateRange.to ? (
                              <span>{formatRegionalDate(dateRange.from, locale)} - {formatRegionalDate(dateRange.to, locale)}</span>
                            ) : (
                              <span>From {formatRegionalDate(dateRange.from, locale)}</span>
                            )
                          ) : (
                            <span>Source Date</span>
                          )}
                          {dateRangeHasValue(dateRange) && (
                            <button
                              type="button"
                              onClick={event => {
                                event.stopPropagation();
                                clearDateRange();
                              }}
                              className="ml-0.5 text-gray-400 hover:text-gray-600"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          )}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-fit max-w-[calc(100vw-2rem)] p-0" align="start" side="bottom" sideOffset={8}>
                        <div className="w-[calc(100vw-2rem)] max-w-[31rem]">
                          <DateRangeCalendar
                            value={dateRange}
                            onChange={setDateRange}
                            initialMonth={dateRange?.from ?? (eventYears.length > 0 ? new Date(eventYears[0], 0, 1) : undefined)}
                            customPresets={eventYears.length > 1 ? yearPresets : undefined}
                          />
                        </div>
                      </PopoverContent>
                  </Popover>
                  <div className={OTHER_FILTER_SEPARATOR_CLASSNAME} aria-hidden="true" />
                  <SourceSelectionPopover
                    counts={sourceCounts}
                    activeSources={activeSources}
                    onToggleSource={toggleSource}
                    onClearSources={() => setActiveSources(new Set())}
                  />
                </div>
              </div>
            </div>

            <div className="px-6 pt-4 flex items-start justify-between gap-4">
              <div className="min-w-0 flex-1 max-w-[88rem]">
                <EntityFilterSummary
                  entitiesCount={entities.length}
                  filteredEntitiesCount={filteredEntities.length}
                  hasActiveFilters={hasActiveFilters}
                  activeSources={activeSources}
                  dateRange={dateRange}
                  locale={locale}
                  onToggleSource={toggleSource}
                  onClearDateRange={clearDateRange}
                  onClearAllFilters={clearAllFilters}
                />
                <OutputDisclaimer className="mt-2 text-left" />
              </div>
            </div>

            {/* Grouped entity list */}
            <div className="w-full max-w-[88rem] px-6 py-4 space-y-4">
              {allGroups.map(group => {
                if (activeFilters.size > 0 && !activeFilters.has(group.key)) return null;
                const groupEntities = groupedEntities.get(group.key) || [];
                if (groupEntities.length === 0) return null;
                const isCollapsed = collapsedGroups.has(group.key);

                return (
                  <div key={group.key}>
                    <button
                      type="button"
                      onClick={() => toggleGroup(group.key)}
                      className="group mb-2 flex w-full items-center gap-1.5 text-left"
                    >
                      {isCollapsed
                        ? <ChevronRight className="h-3.5 w-3.5 text-gray-400" />
                        : <ChevronDown className="h-3.5 w-3.5 text-gray-400" />
                      }
                      <span className="text-[11px] font-semibold uppercase tracking-widest text-gray-400 group-hover:text-gray-500">
                        {group.label}
                      </span>
                      <span className="text-[10px] text-gray-400">{groupEntities.length}</span>
                    </button>
                    {!isCollapsed && (
                      group.showSideAlignment ? (
                        <HierarchicalSideSection
                          entities={groupEntities}
                          selectedId={selectedId}
                          onSelect={handleSelect}
                        />
                      ) : group.key === 'amici' ? (
                        <HierarchicalPrincipalSection
                          entities={groupEntities}
                          selectedId={selectedId}
                          onSelect={handleSelect}
                          groupKey="amici"
                          principalRoleTypes={new Set(['amicus'])}
                        />
                      ) : (
                        <div className={FLAT_ENTITY_LIST_CLASSNAME}>
                          {groupEntities.map(entity => (
                            <EntityCard
                              key={entity.id}
                              entity={entity}
                              role={getRoleForGroup(entity, group.key)}
                              isSelected={selectedId === entity.id}
                              onSelect={() => handleSelect(entity)}
                            />
                          ))}
                        </div>
                      )
                    )}
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <div className="px-6 py-4">
            <div className="text-center py-8 text-gray-400">
              <p className="text-sm">No entities extracted yet.</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
